import { Group, Text } from '@mantine/core';
import { formatMoney } from '../lib/format';

/**
 * The two ways cash is shown on this site, in one place, because both were about to be written twice.
 *
 * This bar holds two real currencies at once. A drawer is not "worth" one number: it holds dollars
 * AND lempiras, and adding them at today's rate produces a figure nobody ever handed over. So a
 * balance is always a pair, always labelled, and the two halves never merge.
 */
export interface CashPair {
  usdMinor: number;
  hnlMinor: number;
}

/**
 * "$ 65.00 · L 1,000.00" — and a zero half is still printed, because "no lempiras in the drawer" is
 * a fact somebody counting needs to see rather than a gap to interpret.
 */
export function MoneyPair({ pair, fw }: { pair: CashPair | null | undefined; fw?: number }) {
  if (!pair) return <Text c="dimmed">—</Text>;
  return (
    <Group gap={6} wrap="nowrap">
      <Text size="sm" fw={fw}>
        {formatMoney(pair.usdMinor, 'USD')}
      </Text>
      <Text size="sm" c="dimmed">
        ·
      </Text>
      <Text size="sm" fw={fw}>
        {formatMoney(pair.hnlMinor, 'HNL')}
      </Text>
    </Group>
  );
}

/**
 * A drawer that came up short is money gone; a drawer that came up over is a mis-ring somebody still
 * has to explain. A signed number says neither out loud, so the word is printed.
 *
 * The sign is counted minus expected — negative is short. The server computes it and never accepts
 * it from a client; if that ever inverted, this screen would accuse the wrong person.
 */
export interface OverShortLabels {
  exact: string;
  short: string;
  over: string;
  notCounted: string;
}

const ENGLISH_OVER_SHORT: OverShortLabels = {
  exact: 'Exact',
  short: 'short',
  over: 'over',
  notCounted: 'Not counted off',
};

export function OverShort({
  minor,
  currency,
  labels = ENGLISH_OVER_SHORT,
}: {
  minor: number | null | undefined;
  currency: string;
  labels?: OverShortLabels;
}) {
  if (minor === null || minor === undefined) return <Text c="dimmed">—</Text>;
  if (minor === 0) return <Text size="sm">{labels.exact}</Text>;
  const short = minor < 0;
  return (
    <Text size="sm" c={short ? 'red' : 'orange'} fw={600}>
      {formatMoney(Math.abs(minor), currency)} {short ? labels.short : labels.over}
    </Text>
  );
}

/** Both currencies of an over-and-short, side by side and never summed. */
export function OverShortPair({
  pair,
  labels = ENGLISH_OVER_SHORT,
}: {
  pair: CashPair | null | undefined;
  labels?: OverShortLabels;
}) {
  if (!pair) return <Text c="dimmed">{labels.notCounted}</Text>;
  return (
    <Group gap={10} wrap="nowrap">
      <OverShort minor={pair.usdMinor} currency="USD" labels={labels} />
      <Text size="sm" c="dimmed">
        ·
      </Text>
      <OverShort minor={pair.hnlMinor} currency="HNL" labels={labels} />
    </Group>
  );
}
